'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Activity {
  id: string;
  type: 'confirmation' | 'update' | 'alert' | 'cancellation';
  message: string;
  pnr: string;
  timestamp: string;
}

interface RealtimeActivityFeedProps {
  activities: Activity[];
}

const RealtimeActivityFeed = ({ activities }: RealtimeActivityFeedProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'confirmation':
        return { icon: 'CheckCircleIcon', color: 'text-success' };
      case 'update':
        return { icon: 'ArrowPathIcon', color: 'text-primary' };
      case 'alert':
        return { icon: 'ExclamationTriangleIcon', color: 'text-warning' };
      case 'cancellation':
        return { icon: 'XCircleIcon', color: 'text-error' };
      default:
        return { icon: 'InformationCircleIcon', color: 'text-muted-foreground' };
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-brand p-6 border border-border">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex space-x-3">
              <div className="w-10 h-10 bg-muted rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-brand p-6 border border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Real-Time Activity
        </h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-xs text-muted-foreground">Live Updates</span>
        </div>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities.map((activity) => {
          const { icon, color } = getActivityIcon(activity.type);
          return (
            <div
              key={activity.id}
              className="flex items-start space-x-3 p-3 bg-surface rounded-lg hover:bg-muted transition-colors duration-200"
            >
              <div className={`w-10 h-10 rounded-full bg-card flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon name={icon as any} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground mb-1">
                  {activity.message}
                </p>
                <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                  <span className="font-mono">{activity.pnr}</span>
                  <span>•</span>
                  <span>{activity.timestamp}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RealtimeActivityFeed;